import type { SessionTimeSeriesEntry } from '@lms/api-client/backend';
import {
  ChartCard,
  formatNumber,
  type SeriesSet,
  StackedTimeChart,
  stepFor,
} from '@lms/charts';
import { Tabs, TabsList, TabsTrigger } from '@lms/ui/tabs';
import { useState } from 'react';
import { useLocale, useTranslations } from 'use-intl';
import { WIDE } from '@/features/dashboard/components/stats-layout';
import type { StatsHookParams } from '@/features/dashboard/use-stats';

const curriculumOf = (r: { curriculumName: string }) => r.curriculumName;
const countOf = (r: { count: number }) => r.count;
const periodOf = (r: { period: string }) => r.period;

/**
 * Completed trainings over the selected period, per curriculum: stacked
 * columns per bucket, or stacked areas for the trend. Last year's figures
 * (`previous`) are only drawn on the columns.
 */
export function CompletedTrainingsCard({
  sessions,
  previous,
  seriesSet,
  filter,
  periodLabel,
}: {
  sessions: readonly SessionTimeSeriesEntry[];
  previous?: { data: readonly SessionTimeSeriesEntry[]; label: string };
  seriesSet: SeriesSet;
  filter: Pick<StatsHookParams, 'period' | 'fromDate'>;
  periodLabel: string;
}) {
  const t = useTranslations('stats');
  const locale = useLocale();
  const [view, setView] = useState<'bars' | 'area'>('bars');
  const total = sessions.reduce((sum, s) => sum + s.count, 0);

  return (
    <ChartCard
      title={t('completed_trainings')}
      description={periodLabel}
      value={formatNumber(total, locale)}
      center={
        <Tabs value={view} onValueChange={(v) => setView(v as 'bars' | 'area')}>
          <TabsList>
            <TabsTrigger value="bars">
              {stepFor(filter.period) === 'day' ? t('per_day') : t('per_month')}
            </TabsTrigger>
            <TabsTrigger value="area">{t('trend')}</TabsTrigger>
          </TabsList>
        </Tabs>
      }
      className={WIDE}
    >
      <StackedTimeChart
        data={sessions}
        period={periodOf}
        series={curriculumOf}
        value={countOf}
        seriesSet={seriesSet}
        variant={view}
        range={{
          period: filter.period,
          fromDate: filter.fromDate ?? new Date(),
        }}
        comparison={previous}
        locale={locale}
        emptyLabel={t('no_data')}
        totalLabel={t('total')}
      />
    </ChartCard>
  );
}
